import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AedesTable from "../components/AedesTable";
import {
  Fab,
  List,
  ListItem,
  ListItemButton,
  Popover,
  TextField,
  InputAdornment,
  Box,
  Typography,
  Button,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  SelectChangeEvent,
  Badge,
  ButtonGroup,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import dayjs, { Dayjs } from "dayjs";
import { useAllCollectionsForFilter } from "../hooks/useQueries";

interface Collection {
  id: number;
  name: string;
  description?: string;
  color?: string;
}

export const ViewTable: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [siteIdFilter, setSiteIdFilter] = useState("");
  const [descriptorIdFilter, setDescriptorIdFilter] = useState("");
  const [collectionFilter, setCollectionFilter] = useState<number[]>([]);
  const navigate = useNavigate();

  const { data: collections = [] } = useAllCollectionsForFilter();

  useEffect(() => {
    document.title = "ACTT | View";
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (link: string) => {
    handleClose();
    navigate(link);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleCollectionChange = (event: SelectChangeEvent<number[]>) => {
    const value = event.target.value;
    setCollectionFilter(typeof value === 'string' ? [] : value);
  };

  const clearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setSiteIdFilter("");
    setDescriptorIdFilter("");
    setCollectionFilter([]);
  };

  const hasActiveFilters = startDate || endDate || siteIdFilter || descriptorIdFilter || collectionFilter.length > 0;
  
  const activeFilterCount = [
    startDate,
    endDate,
    siteIdFilter,
    descriptorIdFilter,
    collectionFilter.length > 0,
  ].filter(Boolean).length;

  const setDatePreset = (preset: string) => {
    const today = dayjs();
    switch (preset) {
      case "last7":
        setStartDate(today.subtract(7, "day"));
        setEndDate(today);
        break;
      case "last30":
        setStartDate(today.subtract(30, "day"));
        setEndDate(today);
        break;
      case "thisMonth":
        setStartDate(today.startOf("month"));
        setEndDate(today.endOf("month"));
        break;
      case "lastMonth":
        setStartDate(today.subtract(1, "month").startOf("month"));
        setEndDate(today.subtract(1, "month").endOf("month"));
        break;
      default:
        break;
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  return (
    <div className="App">
      <Box sx={{ p: 2 }}>
        <Typography variant="h4" gutterBottom>
          Image Database
        </Typography>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search images..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {/* Filter Toggle */}
        <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center" }}>
          <Button
            variant="outlined"
            startIcon={
              <Badge badgeContent={activeFilterCount} color="primary">
                <FilterListIcon />
              </Badge>
            }
            onClick={() => setShowFilters(!showFilters)}
            color={hasActiveFilters ? "primary" : "inherit"}
          >
            {showFilters ? "Hide Filters" : "Show Filters"}
            {hasActiveFilters && " (Active)"}
          </Button>
          {hasActiveFilters && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              size="small"
            >
              Clear All Filters
            </Button>
          )}
        </Box>

        {/* Active Filter Chips */}
        {hasActiveFilters && (
          <Box sx={{ mb: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
            {startDate && (
              <Chip
                label={`From: ${startDate.format("MM/DD/YYYY")}`}
                onDelete={() => setStartDate(null)}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            {endDate && (
              <Chip
                label={`To: ${endDate.format("MM/DD/YYYY")}`}
                onDelete={() => setEndDate(null)}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            {siteIdFilter && (
              <Chip
                label={`Site: ${siteIdFilter}`}
                onDelete={() => setSiteIdFilter("")}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            {descriptorIdFilter && (
              <Chip
                label={`Descriptor: ${descriptorIdFilter}`}
                onDelete={() => setDescriptorIdFilter("")}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            {collectionFilter.length > 0 && (
              <Chip
                label={`${collectionFilter.length} Collection(s)`}
                onDelete={() => setCollectionFilter([])}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            {hasActiveFilters && (
              <Chip
                label="Clear All"
                onClick={clearFilters}
                size="small"
                color="error"
                variant="outlined"
              />
            )}
          </Box>
        )}

        <Collapse in={showFilters}>
          <Box
            sx={{
              mb: 3,
              p: 2,
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              backgroundColor: "#f9f9f9",
            }}
          >
            <Typography variant="h6" gutterBottom>
              Filter Options
            </Typography>
            
            {/* Date Range Presets */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Quick Date Range:
              </Typography>
              <ButtonGroup variant="outlined" size="small">
                <Button onClick={() => setDatePreset("last7")}>Last 7 Days</Button>
                <Button onClick={() => setDatePreset("last30")}>Last 30 Days</Button>
                <Button onClick={() => setDatePreset("thisMonth")}>This Month</Button>
                <Button onClick={() => setDatePreset("lastMonth")}>Last Month</Button>
              </ButtonGroup>
            </Box>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {/* Date Range */}
                <Box sx={{ flex: '1 1 300px', minWidth: '200px' }}>
                  <DatePicker
                    label="Start Picture Date"
                    value={startDate}
                    onChange={(newValue) => setStartDate(newValue)}
                    slotProps={{
                      textField: { fullWidth: true, variant: "outlined" },
                    }}
                  />
                </Box>
                <Box sx={{ flex: '1 1 300px', minWidth: '200px' }}>
                  <DatePicker
                    label="End Picture Date"
                    value={endDate}
                    onChange={(newValue) => setEndDate(newValue)}
                    slotProps={{
                      textField: { fullWidth: true, variant: "outlined" },
                    }}
                  />
                </Box>

                {/* Site ID Filter */}
                <Box sx={{ flex: '1 1 300px', minWidth: '200px' }}>
                  <TextField
                    fullWidth
                    label="Site ID"
                    value={siteIdFilter}
                    onChange={(e) => setSiteIdFilter(e.target.value)}
                    variant="outlined"
                  />
                </Box>

                {/* Descriptor ID Filter */}
                <Box sx={{ flex: '1 1 300px', minWidth: '200px' }}>
                  <TextField
                    fullWidth
                    label="Descriptor ID"
                    value={descriptorIdFilter}
                    onChange={(e) => setDescriptorIdFilter(e.target.value)}
                    variant="outlined"
                    type="number"
                  />
                </Box>

                {/* Collection Filter */}
                {collections.length > 0 && (
                  <Box sx={{ flex: '1 1 100%' }}>
                    <FormControl fullWidth>
                      <InputLabel>Collections</InputLabel>
                      <Select
                        multiple
                        value={collectionFilter}
                        onChange={handleCollectionChange}
                        input={<OutlinedInput label="Collections" />}
                        renderValue={(selected) => (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {selected.map((value) => {
                              const collection = collections.find((c) => c.id === value);
                              return (
                                <Chip
                                  key={value}
                                  label={collection?.name || value}
                                  size="small"
                                />
                              );
                            })}
                          </Box>
                        )}
                      >
                        {collections.map((collection) => (
                          <MenuItem key={collection.id} value={collection.id}>
                            {collection.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}
              </Box>
            </LocalizationProvider>
          </Box>
        </Collapse>

        <AedesTable 
          searchTerm={searchTerm}
          startDate={startDate?.toDate() || null}
          endDate={endDate?.toDate() || null}
          siteIdFilter={siteIdFilter}
          descriptorIdFilter={descriptorIdFilter ? parseInt(descriptorIdFilter) : undefined}
          collectionIds={collectionFilter}
        />
      </Box>
      <Fab
        color="primary"
        aria-label="add"
        onClick={handleClick}
        sx={{
          position: "fixed",
          bottom: 20,
          right: 20,
          zIndex: 1000,
        }}
      >
        <AddIcon />
      </Fab>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <List>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleMenuItemClick("/yolo")}>
              YOLO
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleMenuItemClick("/yolocloud")}>
              YOLO (Cloud)
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleMenuItemClick("/add")}>
              OpenCV
            </ListItemButton>
          </ListItem>
        </List>
      </Popover>
    </div>
  );
};

export default ViewTable;
